/* eslint-disable indent */
"use client";

import { useContext, useState } from "react";
import { toast } from "react-toastify";
import { HeartFilledIcon } from "@radix-ui/react-icons";
import { CheckCircle, Clock, Heart, XCircle } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { likeLora } from "@/lib/actions";
import { AppContext } from "@/lib/contexts";
import { AppReducerActionType, LoraDTO, LoraStatus } from "@/types";
interface LoraCardProps {
  lora: LoraDTO;
}
export default function LoraCard({
  lora
}: LoraCardProps) {
  const {
    state,
    dispatch
  } = useContext(AppContext);
  const [editedLora, setEditedLora] = useState<LoraDTO>(lora);
  const getStatusIcon = (status?: LoraStatus) => {
    switch (status) {
      case LoraStatus.SUBMITTED:
        return <Clock className="h-4 w-4 text-alert" />;
      case LoraStatus.READY:
        return <CheckCircle className="h-4 w-4 text-success" />;
      case LoraStatus.ERROR:
        return <XCircle className="h-4 w-4 text-error" />;
      default:
        return null;
    }
  };
  const handleLike = async () => {
    if (!state.isLoggedIn) {
      dispatch({
        type: AppReducerActionType.SHOW_AUTH_MODAL,
        payload: {
          ...state,
          showAuthModal: true
        }
      });
      return;
    }
    if (editedLora.isLiked) {
      setEditedLora(prevLora => {
        if (!prevLora) return prevLora;
        return {
          ...prevLora,
          likes: prevLora.likes - 1,
          isLiked: false
        };
      });
    } else {
      setEditedLora(prevLora => {
        if (!prevLora) return prevLora;
        return {
          ...prevLora,
          likes: prevLora.likes + 1,
          isLiked: true
        };
      });
    }
    const response = await likeLora(editedLora.id);
    if (response.success) {
      if (!editedLora.isLiked) {
        toast.success("Liked LORA");
      } else {
        toast.success("Unliked LORA");
      }
    } else {
      toast.error("Failed to like LORA");
    }
  };
  return <Card className="group relative overflow-hidden bg-background/50 backdrop-blur-sm !border-slate-400/30 hover:border-primary/30 transition-all duration-300" data-sentry-element="Card" data-sentry-component="LoraCard" data-sentry-source-file="index.tsx">
            <div className="relative aspect-square overflow-hidden">
                <Link href={`/loras/${editedLora.id}`} data-sentry-element="Link" data-sentry-source-file="index.tsx">
                    <Image src={editedLora.coverImage || "/placeholder.svg"} alt={editedLora.loraName} layout="fill" objectFit="cover" className="object-cover w-full h-full transition-transform duration-300 group-hover:scale-105" data-sentry-element="Image" data-sentry-source-file="index.tsx" />
                </Link>
            </div>
            <div className="p-4">
                <Link href={`/loras/${editedLora.id}`} data-sentry-element="Link" data-sentry-source-file="index.tsx">
                    <div className="flex justify-between items-start mb-2">
                        <h3 className="text-xl font-semibold text-secondary">
                            {editedLora.loraName}
                        </h3>
                        {lora.status && <Badge variant="outline" className={`flex items-center gap-1 ${editedLora.status === LoraStatus.READY ? "text-green-500 border-green-500" : editedLora.status === LoraStatus.SUBMITTED ? "text-gray-500 border-gray-500" : editedLora.status === LoraStatus.ERROR ? "text-red-500 border-red-500" : "text-gray-300 border-slate-700"}`}>
                                {getStatusIcon(editedLora.status)}
                                {editedLora.status}
                            </Badge>}
                    </div>
                    <p className="text-sm text-gray-400 mb-3 line-clamp-2">
                        {editedLora.shortDescription}
                    </p>
                    <div className="flex flex-wrap gap-1">
                        <Badge key={editedLora.triggerWord} variant="secondary" className="bg-primary/10 hover:bg-primary/20 text-primary !break-all" data-sentry-element="Badge" data-sentry-source-file="index.tsx">
                            {editedLora.triggerWord}
                        </Badge>
                    </div>
                    {editedLora.weight && <div className="flex flex-wrap gap-1 mt-2">
                            <Badge key={editedLora.weight} variant="secondary" className="bg-slate-700 hover:bg-slate-800 text-primary !break-all">
                                Lora Weight: {editedLora.weight}
                            </Badge>
                        </div>}
                    <div className="flex flex-wrap gap-2 mb-4">
                        {editedLora.tags && editedLora.tags.map((tag, index) => <Badge key={`${editedLora.id}-${tag}-${index}`} variant="secondary" className="bg-secondary/20 text-secondary-light px-2 py-1 rounded-full text-xs font-medium">
                                    {tag}
                                </Badge>)}
                    </div>
                </Link>
                <div className="flex justify-between items-center">
                    <Button variant="ghost" size="sm" className="flex items-center text-muted-foreground" onClick={handleLike} data-sentry-element="Button" data-sentry-source-file="index.tsx">
                        {editedLora.isLiked ? <HeartFilledIcon className="text-rose-500" /> : <Heart className="h-4 w-4 fill-none" />}
                        {editedLora.likes}
                    </Button>
                    {editedLora.isPublic === false && <Badge variant="outline" className="text-grey-light border-grey">
                            Private
                        </Badge>}
                </div>
            </div>
        </Card>;
}